import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { favoriteService } from '../services/dataService';
import type { FavoriteEntityType } from '@conference/shared';
import { useEventData } from '../context/EventDataContext';

// ─── Core favorites hook (Supabase-backed) ────────────────────────────────────

export function useFavorites() {
  const { authUser, authSession, currentEventId, isAuthSessionCurrent } = useApp();
  const { preview } = useEventData();
  const userId = authUser?.id ?? null;
  const eventScopeRef = useRef({ eventId: currentEventId });
  if (eventScopeRef.current.eventId !== currentEventId) {
    eventScopeRef.current = { eventId: currentEventId };
  }

  // Set of "type:id" strings for O(1) lookup
  const [favoriteSet, setFavoriteSet] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const key = (id: string, type: FavoriteEntityType) => `${type}:${id}`;

  // Load from Supabase when user changes
  useEffect(() => {
    if (preview || !userId) {
      setFavoriteSet(new Set());
      return;
    }
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    let cancelled = false;
    setFavoriteSet(new Set());
    setLoading(true);
    favoriteService.getAll(userId, currentEventId, { signal: owner.signal }).then((favorites) => {
      if (cancelled || !isAuthSessionCurrent(owner)) return;
      setFavoriteSet(new Set(favorites.map((f) => key(f.entity_id, f.entity_type))));
    }).catch((err) => {
      console.error('Failed to load favorites', err);
    }).finally(() => {
      if (!cancelled && isAuthSessionCurrent(owner)) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [authSession, currentEventId, isAuthSessionCurrent, preview, userId]);

  const isFavorite = useCallback(
    (id: string, type: FavoriteEntityType): boolean => favoriteSet.has(key(id, type)),
    [favoriteSet]
  );

  const addFavorite = useCallback(async (id: string, type: FavoriteEntityType) => {
    const owner = authSession;
    const eventScope = eventScopeRef.current;
    if (
      preview
      || !userId
      || !owner
      || !isAuthSessionCurrent(owner)
      || favoriteSet.has(key(id, type))
      || (type === 'attendee' && !currentEventId)
    ) return;
    // Optimistic update
    setFavoriteSet((prev) => new Set([...prev, key(id, type)]));
    try {
      await favoriteService.add(
        userId,
        type,
        id,
        type === 'attendee' ? currentEventId : null,
        { signal: owner.signal },
      );
      if (!isAuthSessionCurrent(owner)) return;
    } catch (err) {
      console.error('Failed to add favorite', err);
      // Revert
      if (isAuthSessionCurrent(owner) && eventScopeRef.current === eventScope) {
        setFavoriteSet((prev) => { const next = new Set(prev); next.delete(key(id, type)); return next; });
      }
    }
  }, [authSession, currentEventId, favoriteSet, isAuthSessionCurrent, preview, userId]);

  const removeFavorite = useCallback(async (id: string, type: FavoriteEntityType) => {
    const owner = authSession;
    const eventScope = eventScopeRef.current;
    if (preview || !userId || !owner || !isAuthSessionCurrent(owner) || !favoriteSet.has(key(id, type))) return;
    // Optimistic update
    setFavoriteSet((prev) => { const next = new Set(prev); next.delete(key(id, type)); return next; });
    try {
      await favoriteService.remove(userId, type, id, { signal: owner.signal });
      if (!isAuthSessionCurrent(owner)) return;
    } catch (err) {
      console.error('Failed to remove favorite', err);
      // Revert
      if (isAuthSessionCurrent(owner) && eventScopeRef.current === eventScope) {
        setFavoriteSet((prev) => new Set([...prev, key(id, type)]));
      }
    }
  }, [authSession, favoriteSet, isAuthSessionCurrent, preview, userId]);

  const toggleFavorite = useCallback(async (id: string, type: FavoriteEntityType) => {
    if (isFavorite(id, type)) {
      await removeFavorite(id, type);
    } else {
      await addFavorite(id, type);
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  const getFavoriteIdsByType = useCallback(
    (type: FavoriteEntityType): string[] => {
      const prefix = `${type}:`;
      return [...favoriteSet]
        .filter((k) => k.startsWith(prefix))
        .map((k) => k.slice(prefix.length));
    },
    [favoriteSet]
  );

  return {
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    getFavoriteIdsByType,
    totalFavorites: favoriteSet.size,
    loading,
  };
}

// ─── Convenience wrappers (same API as before) ────────────────────────────────

export function useSessionFavorites() {
  const { isFavorite, toggleFavorite, getFavoriteIdsByType } = useFavorites();
  return {
    isSessionFavorite: (id: string) => isFavorite(id, 'session'),
    toggleSessionFavorite: (id: string) => toggleFavorite(id, 'session'),
    favoriteSessionIds: getFavoriteIdsByType('session'),
  };
}

export function usePaperFavorites() {
  const { isFavorite, toggleFavorite, getFavoriteIdsByType } = useFavorites();
  return {
    isPaperFavorite: (id: string) => isFavorite(id, 'paper'),
    togglePaperFavorite: (id: string) => toggleFavorite(id, 'paper'),
    favoritePaperIds: getFavoriteIdsByType('paper'),
  };
}

export function useSpeakerFavorites() {
  const { isFavorite, toggleFavorite, getFavoriteIdsByType } = useFavorites();
  return {
    isSpeakerFavorite: (id: string) => isFavorite(id, 'speaker'),
    toggleSpeakerFavorite: (id: string) => toggleFavorite(id, 'speaker'),
    favoriteSpeakerIds: getFavoriteIdsByType('speaker'),
  };
}

export function useExhibitorFavorites() {
  const { isFavorite, toggleFavorite, getFavoriteIdsByType } = useFavorites();
  return {
    isExhibitorFavorite: (id: string) => isFavorite(id, 'exhibitor'),
    toggleExhibitorFavorite: (id: string) => toggleFavorite(id, 'exhibitor'),
    favoriteExhibitorIds: getFavoriteIdsByType('exhibitor'),
  };
}
